import React, { useEffect, useMemo, useState } from "react";
import { StepUpSpinner } from "../../../Components/Loader";
import {
  Breadcrumbs,
  CircularProgress,
  Grid,
  Box,
  Button,
  TextField,
  InputAdornment,
  Card,
  Typography,
  Chip,
  IconButton,
  Link as MuiLink,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Stack,
  useTheme,
  alpha,
  Autocomplete,
  Checkbox,
} from "@mui/material";
import {
  useGetProgramFolderQuery,
  useGetProgramQuery,
  useCreateProgramFolderMutation,
  useDeleteProgramFolderMutation,
  useMoveProgramToFolderMutation,
  useMoveFolderMutation,
  useCloneProgramMutation,
  useLockProgramMutation,
  useUnlockProgramMutation,
  useDeleteProgramMutation,
  useGetProgramLibraryMetricsQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useConfirm, confirmPresets } from "../../../Components/Confirm";
import { CiSearch } from "react-icons/ci";
import { Link, useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FolderIcon from "@mui/icons-material/Folder";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CopyToClientsDialog from "../shared/CopyToClientsDialog";
import ImportProgramsDialog from "../shared/ImportProgramsDialog";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EventNoteIcon from "@mui/icons-material/EventNote";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import useIsMobile from "../../../Core/hook/useIsMobile";
import {
  LegacyTokens, TypeScale, PageSurface, resolveStatusTriad, programStatusLabel,
  B13, B13Ink, B13TypeScale, B13Status, resolveB13Status, tint12,
} from "../../../theme/b13Tokens";
import Tooltip from "@mui/material/Tooltip";

// Backend column names, not renamed. Batch 30 removed the mis-mapped
// `name` alias - the API returns `folderName` and always did. IFolder also
// exposes parentFolderId + programCount (populated by ProgramFolderController::index).
interface IFolder {
  id: string;
  folderName: string;
  parentFolderId?: string | null;
  programCount?: number;
  children?: IFolder[];
}

interface IProgram {
  id: string;
  programName: string;
  tags?: string[];
  tagsWithColour?: Array<{ tagId: number; name: string; colour: string | null }>;
  authorName?: string;
  status: string;
  folderId?: string;
  folderName?: string;
  studentId?: string | null;
  studentName?: string;
  category?: string;              // Batch 62 "1 " legacy mirror; prefer treatmentAreaName
  treatmentAreaName?: string | null;
  treatment_area_id?: number | null;
  measurementType?: string;
  targetCount?: number;
  lastSessionDate?: string | null;
}

interface ILibraryMetrics {
  countsByStatus: Record<string, number>;
  studentsWithActive: number;
  sessionsLast7Days: number;
  totalPrograms: number;
  activePrograms?: number;      // Batch 31
  totalTargets: number;
  totalFolders: number;
  // Batch 31 Phase 1 additions:
  assignedStudents?: number;
  activeTargets?: number;
  masteredTargets?: number;
  sessionsThisWeek?: number;
  prerequisites: {
    promptTemplateCount: number;
    masteryWorkflowCount: number;
  };
}

const STATUS_META: Array<{ key: string; label: string; color: "success" | "info" | "warning" | "default" | "primary" | "error" }> = [
  { key: "baseline", label: "Baseline", color: "info" },
  { key: "active", label: "Active", color: "primary" },
  { key: "maintenance", label: "Maintenance", color: "success" },
  { key: "mastered", label: "Mastered", color: "success" },
  { key: "hold", label: "Hold", color: "warning" },
];


// Recursive folder tree node. Batch 30:
//   - reads folder.folderName (matches backend column)
//   - per-node three-dot menu with Add subfolder / Rename / Move / Delete
//   - shows programCount for the folder alongside the label
//   - collapse chevron always visible; a folder with no children shows a
//     dot placeholder so alignment stays consistent across levels
// Return the id set of every ancestor of `id` in the folder tree so
// FolderTreeNode can auto-expand those on mount (used after creating a
// nested folder so the new leaf is visible without collapsing anything else).
function ancestorSetOf(folders: IFolder[], id: string | null | undefined): Set<string> {
  const out = new Set<string>();
  if (!id) return out;
  const parentIndex = new Map<string, string | null>();
  const collect = (list: IFolder[]) => {
    for (const f of list) {
      parentIndex.set(f.id, f.parentFolderId ? String(f.parentFolderId) : null);
      if (f.children?.length) collect(f.children);
    }
  };
  collect(folders);
  let cur: string | null | undefined = parentIndex.get(id) ?? null;
  let steps = 0;
  while (cur && steps++ < 50) {
    out.add(cur);
    cur = parentIndex.get(cur) ?? null;
  }
  return out;
}

// Rasmus-style flat folder row + program row. No card per row, no borders "
// hover background only. The old empty-child hint row (spec "5.2) has been
// row has been removed per - 5.2 (subfolder creation lives in the overflow
// menu). Programs contained in the folder render as child rows beneath any
// subfolders when the folder is expanded.
const FolderTreeNode: React.FC<{
  folder: IFolder;
  selectedFolderId: string | null;
  highlightedFolderId?: string | null;
  autoExpandIds?: Set<string>;
  programsByFolder: Map<string, IProgram[]>;
  onSelect: (id: string) => void;
  onOpenFolder: (id: string) => void;
  onMenu: (event: React.MouseEvent<HTMLElement>, folder: IFolder) => void;
  onProgramMenu: (event: React.MouseEvent<HTMLElement>, program: IProgram) => void;
  onOpenProgram: (program: IProgram) => void;
  onCopyProgramToClients?: (program: IProgram) => void;
  selectedProgramIds?: Set<string>;
  onToggleProgramSelected?: (id: string) => void;
  onSetProgramsSelected?: (ids: string[], selected: boolean) => void;
  level: number;
  expandedFolderIds: Set<string>;
  onToggle: (id: string) => void;
  // When set, only folders whose id appears in this set are rendered.
  // Null means no filter (search inactive).
  visibleFolderIds?: Set<string> | null;
}> = ({
  folder, selectedFolderId, highlightedFolderId, autoExpandIds, programsByFolder,
  onSelect, onOpenFolder, onMenu, onProgramMenu, onOpenProgram, onCopyProgramToClients,
  selectedProgramIds, onToggleProgramSelected, onSetProgramsSelected,
  level, expandedFolderIds, onToggle, visibleFolderIds,
}) => {
  // NOTE: search-active hide-check lives AFTER all hook calls so we
  // never violate rules-of-hooks by returning early.
  const hidden = !!(visibleFolderIds && !visibleFolderIds.has(String(folder.id)));
  const hasSubfolders = !!folder.children?.length;
  const containedPrograms = programsByFolder.get(String(folder.id)) ?? [];
  const hasContents = hasSubfolders || containedPrograms.length > 0;
  const expanded = expandedFolderIds.has(folder.id);
  // Under search, the visible count reflects post-filter matches so
  // "Grade 3 Reading 1" doesn't misrepresent a filtered view. Falls
  // back to backend programCount only when search is inactive.
  const direct = visibleFolderIds ? containedPrograms.length : (folder.programCount ?? containedPrograms.length);
  const isHighlighted = highlightedFolderId === folder.id;

  // Auto-expand ancestors of a freshly-created folder so the new leaf is visible.
  React.useEffect(() => {
    if (autoExpandIds?.has(folder.id) && !expanded) onToggle(folder.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoExpandIds, folder.id]);

  const isSelected = selectedFolderId === folder.id;

  // Batch 1A "1A.7 " cascade selection: walk this folder's descendants
  // (subfolders + contained programs) to collect every program id
  // beneath it. The checkbox toggles the whole set at once.
  const descendantProgramIds = React.useMemo(() => {
    const ids: string[] = [];
    const walk = (f: IFolder) => {
      const rows = programsByFolder.get(String(f.id)) ?? [];
      for (const p of rows) ids.push(String(p.id));
      f.children?.forEach(walk);
    };
    walk(folder);
    return ids;
  }, [folder, programsByFolder]);
  const allDescendantsSelected =
    descendantProgramIds.length > 0 &&
    descendantProgramIds.every((id) => selectedProgramIds?.has(id));
  const anyDescendantSelected =
    descendantProgramIds.some((id) => selectedProgramIds?.has(id));

  // All hooks have run; safe to short-circuit render.
  if (hidden) return null;

  return (
    <>
      <Box
        role="treeitem"
        aria-expanded={hasContents ? expanded : undefined}
        data-testid={`folder-row-${folder.id}`}
        sx={{
          display: "flex", alignItems: "center", gap: 1,
          pl: 1 + level * 2.25, pr: 1,
          minHeight: 44,
          cursor: "pointer",
          transition: "background-color 150ms",
          backgroundColor: isHighlighted
            ? tint12(B13.primary)
            : isSelected ? tint12(B13.primary) : "transparent",
          color: isSelected ? B13.primary : B13Ink.ink2,
          "&:hover": {
            backgroundColor: isSelected ? tint12(B13.primary) : B13.canvas,
          },
        }}
      >
        {onSetProgramsSelected && descendantProgramIds.length > 0 ? (
          <Checkbox
            size="small"
            checked={allDescendantsSelected}
            indeterminate={!allDescendantsSelected && anyDescendantSelected}
            onClick={(e) => e.stopPropagation()}
            onChange={() => onSetProgramsSelected(descendantProgramIds, !allDescendantsSelected)}
            sx={{ p: 0.5, width: 30, flexShrink: 0 }}
            inputProps={{ "aria-label": `Select all programs in ${folder.folderName}` }}
          />
        ) : (
          <Box sx={{ width: 30, flexShrink: 0 }} />
        )}
        <Box
          onClick={(e) => { e.stopPropagation(); if (hasContents) onToggle(folder.id); }}
          sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 20, height: 20 }}
        >
          {hasContents
            ? (expanded
                ? <ExpandMoreIcon fontSize="small" sx={{ color: LegacyTokens.ink3 }} />
                : <ChevronRightIcon fontSize="small" sx={{ color: LegacyTokens.ink3 }} />)
            : null}
        </Box>
        <Box onClick={(e) => { e.stopPropagation(); if (hasContents) onToggle(folder.id); }}
          sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 22 }}>
          {/* Batch 42 "4.2 " folder icon encodes two things from one element:
             brand colour means "there is something in here" (subfolders or
             programs), ink3 means "empty". Opacity encodes open/closed:
             full when expanded, 60% when collapsed. Depth is no longer
             carried by hue " indentation already does that job. */}
          {/* Batch 13 Part B " no folder colour in schema, so every
             folder icon renders in the accent violet. Full-strength
             when contents+expanded, 60% when contents+collapsed, muted
             ink3 when truly empty (nothing to open into). */}
          {(() => {
            const Icon = expanded ? FolderOpenIcon : FolderIcon;
            const color = hasContents ? B13.accent : B13Ink.ink3;
            const opacity = hasContents ? (expanded ? 1 : 0.6) : 1;
            return <Icon fontSize="small" sx={{ color, opacity }} />;
          })()}
        </Box>
        <Box
          onClick={() => { onSelect(folder.id); onOpenFolder(folder.id); }}
          sx={{ flexGrow: 1, minWidth: 0, display: "flex", alignItems: "baseline", gap: 1 }}
        >
          <Typography noWrap sx={{ fontSize: 14, fontWeight: 550, color: isSelected ? B13.primary : B13Ink.ink2 }}>
            {folder.folderName}
          </Typography>
          <Typography component="span" noWrap sx={{ fontSize: 12, color: LegacyTokens.ink3 }}>
            {direct}
          </Typography>
        </Box>
        <Tooltip title="More actions" placement="top" arrow>
          <IconButton
            onClick={(e) => { e.stopPropagation(); onMenu(e, folder); }}
            aria-label={`Folder actions for ${folder.folderName}`}
            sx={{
              width: 36, height: 36, borderRadius: "50%",
              color: LegacyTokens.ink3,
              "&:hover": { backgroundColor: LegacyTokens.canvas2, color: LegacyTokens.ink2 },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Batch 38 "4.1 " 1px indent guide running the full height of this
         folder's children. Sits at the folder's indent column so nesting
         reads without adding weight. */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{
          position: "relative",
          "&::before": {
            content: '""', position: "absolute",
            top: 0, bottom: 0,
            left: `${1 + (level + 1) * 2.25 * 8 + 10}px`, // 8px MUI base - 2.25 + icon centre
            width: "1px",
            backgroundColor: LegacyTokens.line,
          },
        }}>
        {folder.children?.map((child) => (
          <FolderTreeNode
            key={child.id}
            folder={child}
            selectedFolderId={selectedFolderId}
            highlightedFolderId={highlightedFolderId}
            autoExpandIds={autoExpandIds}
            programsByFolder={programsByFolder}
            onSelect={onSelect}
            onOpenFolder={onOpenFolder}
            onMenu={onMenu}
            onProgramMenu={onProgramMenu}
            onOpenProgram={onOpenProgram}
            onCopyProgramToClients={onCopyProgramToClients}
            selectedProgramIds={selectedProgramIds}
            onToggleProgramSelected={onToggleProgramSelected}
            onSetProgramsSelected={onSetProgramsSelected}
            expandedFolderIds={expandedFolderIds}
            onToggle={onToggle}
            level={level + 1}
            visibleFolderIds={visibleFolderIds}
          />
        ))}
        {containedPrograms.map((p) => (
          <ProgramTreeRow key={p.id} program={p} level={level + 1}
            onOpen={() => onOpenProgram(p)}
            onMenu={(e) => onProgramMenu(e, p)}
            onCopyToClients={onCopyProgramToClients}
            selected={selectedProgramIds?.has(String(p.id))}
            onToggleSelect={onToggleProgramSelected} />
        ))}
        {/* Batch 1A "1A.9 " empty-folder inline hint. Sits at the child
           indent so it visually belongs to this folder rather than
           blanking the whole page. Renders only when the folder is
           expanded, has no subfolders and no contained programs. */}
        {!hasSubfolders && containedPrograms.length === 0 && (
          <Box sx={{
            pl: 1 + (level + 1) * 2.25 + 6,
            py: 1,
            fontSize: 12,
            color: LegacyTokens.ink3,
            fontStyle: "italic",
          }}>
            This folder is empty.
          </Box>
        )}
        </Box>
      </Collapse>
    </>
  );
};

// Batch 38 "2 " Mastery Bar. Slim segmented bar showing target-status
// distribution in the - 1.3 dot colours. Full width of its column, 6px tall,
// 3px radius. Segments in dot-order (Mastered, Acquisition, Probe, Hold,
// Waiting, Closed, Discontinued), widths proportional to counts. No gaps
// between segments. Zero-targets ? track only in canvas2. Tooltip lists
// each status + count. Caption line ($2.3) rendered by the caller.
const MASTERY_BAR_ORDER: Array<{ key: string; label: string }> = [
  { key: "mastered",     label: "Mastered" },
  { key: "acquisition",  label: "Acquisition" },
  { key: "probe",        label: "Probe" },
  { key: "hold",         label: "Hold" },
  { key: "waiting",      label: "Waiting" },
  { key: "closed",       label: "Closed" },
  { key: "discontinued", label: "Discontinued" },
];
const MasteryBar: React.FC<{ counts: Record<string, number>; ariaSummary: string }> = ({ counts, ariaSummary }) => {
  const total = MASTERY_BAR_ORDER.reduce((s, k) => s + (counts[k.key] ?? 0), 0);
  // Batch 39 - thicker (8px), rounded, soft canvas2 track for empty state.
  if (total === 0) {
    return (
      <Box
        aria-label={ariaSummary}
        sx={{ width: "100%", height: 8, borderRadius: "999px", backgroundColor: LegacyTokens.canvas2 }}
      />
    );
  }
  const segments = MASTERY_BAR_ORDER
    .map((k) => ({ ...k, count: counts[k.key] ?? 0 }))
    .filter((s) => s.count > 0);
  const tooltipText = segments.map((s) => `${s.label}: ${s.count}`).join(" - ");
  return (
    <Tooltip title={tooltipText} placement="top" arrow>
      <Box
        aria-hidden="true"
        sx={{
          display: "flex", width: "100%", height: 8, borderRadius: "999px", overflow: "hidden",
          backgroundColor: LegacyTokens.canvas2,
        }}
      >
        {segments.map((s) => {
          const t = resolveStatusTriad(s.key);
          return (
            <Box key={s.key} sx={{
              flex: s.count, backgroundColor: t.dot,
              transition: "background-color 150ms",
            }} />
          );
        })}
      </Box>
    </Tooltip>
  );
};

// A single program row rendered inside its containing folder in the tree.
// Batch 38 "4.3 " row now carries the weight: 3px status-dot left accent,
// program name at 15/600 in ink (heavier than folder names " programs are
// the content, folders the container), category " target-count in ink3,
// then the mastery bar + caption line, and the AbaStatusChip on the right.
const ProgramTreeRow: React.FC<{
  program: IProgram;
  level: number;
  onOpen: () => void;
  onMenu: (event: React.MouseEvent<HTMLElement>) => void;
  onCopyToClients?: (program: IProgram) => void;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
}> = ({ program, level, onOpen, onMenu, onCopyToClients, selected, onToggleSelect }) => {
  const targetCount = typeof program.targetCount === "number" ? program.targetCount : 0;
  const author = (program as any).authorName as string | undefined;
  const isLocked = !!(program as any).isLocked;
  // Batch 1A "1A.4 " flat row anatomy. No card, no border, no boxShadow,
  // no icon tile, no MasteryBar. Left to right: (checkbox slot reserved
  // for Priority 5), name, tag chips, owner, primary action (Copy to
  // client), kebab. Hairline divider below via bottom border. Hover
  // highlight spans the full row width.
  //
  // Locked rows: the row body click is intercepted upstream (onOpen is
  // wrapped by the parent to notify + no-op). We still render the row
  // as `cursor: not-allowed` + muted text so the state is obvious
  // before the click, and set aria-disabled so keyboard users don't
  // get an activate-and-nothing-happens feel.
  return (
    <Box
      data-testid={`program-row-${program.id}`}
      role="button"
      aria-disabled={isLocked || undefined}
      sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        pl: 2 + level * 2.25, pr: 1, minHeight: 44,
        cursor: isLocked ? "not-allowed" : "pointer",
        borderBottom: `1px solid ${LegacyTokens.line}`,
        backgroundColor: isLocked ? "rgba(15,23,42,0.025)" : "transparent",
        transition: "background-color 150ms",
        "&:hover": { backgroundColor: isLocked ? "rgba(15,23,42,0.04)" : LegacyTokens.canvas2 },
      }}
      onClick={onOpen}
    >
      {/* Batch 1A "1A.7 " selection checkbox in the first column. When
         no selection handler is passed, falls back to a spacer so the
         name column stays aligned with folder rows. */}
      {onToggleSelect ? (
        <Checkbox
          size="small"
          checked={!!selected}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggleSelect(String(program.id))}
          sx={{ p: 0.5, width: 30, flexShrink: 0 }}
          inputProps={{ "aria-label": `Select ${program.programName}` }}
        />
      ) : (
        <Box sx={{ width: 30, flexShrink: 0 }} />
      )}
      {/* Spacer for the folder's expand chevron + folder icon columns so
         program-row name column aligns with folder-row name column at
         the same indent level. */}
      <Box sx={{ width: 20 + 22 + 8, flexShrink: 0 }} />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: isLocked ? LegacyTokens.ink3 : LegacyTokens.ink }}>
          {program.programName}
        </Typography>
        {(program.treatmentAreaName || targetCount > 0) && (
          <Typography noWrap sx={{ fontSize: 11, color: LegacyTokens.ink3 }}>
            {[program.treatmentAreaName ?? null,
              targetCount > 0 ? `${targetCount} target${targetCount === 1 ? "" : "s"}` : null,
             ].filter(Boolean).join(" - ")}
          </Typography>
        )}
      </Box>
      {(() => {
        // Batch 14 Step 3 - tag chips are keyed by tagId and read
        // name+colour from tagsWithColour (backend joins the pivot).
        // A rename or colour change in Settings propagates on the
        // next fetch. Legacy program.tags (string[]) is a fallback
        // only for rows the endpoint hasn't re-populated yet;
        // synthesised entries there get a synthetic tagId of -i-1 so
        // React keys stay unique without pretending to be real.
        type RichTag = { tagId: number; name: string; colour: string | null };
        const richTags: RichTag[] = Array.isArray(program.tagsWithColour) && program.tagsWithColour.length > 0
          ? program.tagsWithColour
          : (Array.isArray(program.tags)
              ? program.tags.map((t, i) => ({ tagId: -(i + 1), name: t, colour: null }))
              : []);
        if (richTags.length === 0) return null;
        const shown = richTags.slice(0, 3);
        const hiddenCount = richTags.length - shown.length;
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{
            display: { xs: "none", md: "flex" }, maxWidth: 200, rowGap: 0.5, flexShrink: 0,
          }}>
            {shown.map((t) => (
              <AbaTagChip key={t.tagId} label={t.name} colour={t.colour} />
            ))}
            {hiddenCount > 0 && (
              <Tooltip title={richTags.slice(3).map((t) => t.name).join(", ")} arrow>
                <Box component="span" sx={{
                  display: "inline-flex", alignItems: "center",
                  backgroundColor: B13.canvas, color: B13Ink.ink3,
                  fontSize: 11, fontWeight: 600,
                  px: 0.75, py: 0.15,
                  borderRadius: `${LegacyTokens.radius.chip}px`,
                }}>+{hiddenCount}</Box>
              </Tooltip>
            )}
          </Stack>
        );
      })()}
      {author && (
        <Tooltip title={author} arrow>
          <Typography noWrap sx={{
            display: { xs: "none", lg: "block" },
            width: 120, minWidth: 120, flexShrink: 0,
            fontSize: 12, color: LegacyTokens.ink3,
          }}>{author}</Typography>
        </Tooltip>
      )}
      <AbaStatusChip status={program.status} />
      {isLocked && (
        <Tooltip title="Locked program — cannot be opened or edited." placement="top" arrow>
          <Chip
            size="small"
            icon={<LockOutlinedIcon sx={{ fontSize: "14px !important" }} />}
            label="Locked"
            aria-label={`${program.programName} is locked`}
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 700,
              bgcolor: "rgba(15,23,42,0.06)",
              color: LegacyTokens.ink2,
              border: `1px solid ${LegacyTokens.line}`,
              "& .MuiChip-icon": { color: LegacyTokens.ink2, ml: "6px" },
              "& .MuiChip-label": { px: 0.75 },
              flexShrink: 0,
            }}
          />
        </Tooltip>
      )}
      {onCopyToClients && (
        <Tooltip title="Copy to client(s)" placement="top" arrow>
          <IconButton
            onClick={(e) => { e.stopPropagation(); onCopyToClients(program); }}
            aria-label={`Copy ${program.programName} to clients`}
            sx={{
              width: 32, height: 32, borderRadius: `${LegacyTokens.radius.chip}px`,
              color: B13.primary,
              "&:hover": { backgroundColor: tint12(B13.primary) },
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="More actions" placement="top" arrow>
        <IconButton
          onClick={(e) => { e.stopPropagation(); onMenu(e); }}
          aria-label={`Program actions for ${program.programName}`}
          sx={{
            width: 32, height: 32, borderRadius: `${LegacyTokens.radius.chip}px`,
            color: LegacyTokens.ink3,
            "&:hover": { backgroundColor: LegacyTokens.canvas2, color: LegacyTokens.ink2 },
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// Batch 13 Part B - status chip driven by the B13Status semantic
// map. Background is a 12% tint of the accent; text, dot and outline
// all use the full-strength accent. Every status wears its own colour;
// only `waiting` is deliberately slate ("nothing happening here").
const AbaStatusChip: React.FC<{ status: string | null | undefined }> = ({ status }) => {
  const s = resolveB13Status(status);
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", gap: 0.75,
      backgroundColor: tint12(s.accent),
      color: s.accent,
      borderRadius: `${LegacyTokens.radius.chip}px`,
      fontSize: 12, fontWeight: 600, lineHeight: 1.2,
      px: 1, py: 0.25, flexShrink: 0,
      fontVariantNumeric: "tabular-nums",
    }}>
      <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: s.accent }} />
      {s.label}
    </Box>
  );
};

// Batch 13 Part B "3 " tag chip driven by the backend-supplied colour
// from the `tags` table (via program.tagsWithColour, additive endpoint
// shape). Uncoloured tags (name has no matching tags row, or colour is
// null) fall back to a neutral chip on the canvas surface so an
// unowned tag never masquerades as semantic. NO hash - an arbitrary-
// but-pretty colour implies a system that is not there and violates
// "colour means state".
const AbaTagChip: React.FC<{ label: string; colour?: string | null }> = ({ label, colour }) => {
  const c = colour && /^#([0-9a-f]{3}){1,2}$/i.test(colour) ? colour : null;
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center",
      backgroundColor: c ? tint12(c) : B13.canvas,
      color: c ?? B13Ink.ink3,
      fontSize: 11, fontWeight: 600,
      px: 0.75, py: 0.15,
      borderRadius: `${LegacyTokens.radius.chip}px`,
    }}>{label}</Box>
  );
};

const MoveProgramDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  folders: IFolder[];
  onMove: (folderId: string) => void;
}> = ({ open, onClose, folders, onMove }) => {
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const render = (items: IFolder[], level: number): React.ReactNode =>
    items.map((folder) => (
      <React.Fragment key={folder.id}>
        <ListItemButton
          selected={selectedFolder === folder.id}
          onClick={() => setSelectedFolder(folder.id)}
          sx={{ pl: 2 + level * 2 }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <FolderIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={folder.folderName} />
        </ListItemButton>
        {folder.children && render(folder.children, level + 1)}
      </React.Fragment>
    ));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Move Program to Folder</DialogTitle>
      <DialogContent dividers>
        <List dense>{render(folders, 0)}</List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!selectedFolder}
          onClick={() => {
            if (selectedFolder) {
              onMove(selectedFolder);
              onClose();
            }
          }}
        >
          Move
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// A single stat card in the dashboard header.
const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  accent?: string;
  hint?: string;
}> = ({ label, value, icon, accent, hint }) => {
  const theme = useTheme();
  const color = accent || theme.palette.primary.main;
  return (
    <Card
      sx={{
        p: 2,
        display: "flex",
        alignItems: "center",
        gap: 2,
        borderRadius: 2,
        borderLeft: `3px solid ${color}`,
        height: "100%",
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          width: 42, height: 42, borderRadius: 1.5,
          display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: alpha(color, 0.12), color,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
          {value}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
          {label}
        </Typography>
        {hint && (
          <Typography variant="caption" sx={{ display: "block", color: "text.disabled" }}>
            {hint}
          </Typography>
        )}
      </Box>
    </Card>
  );
};

// Onboarding checklist for the empty state.
const PrereqChecklist: React.FC<{
  metrics: ILibraryMetrics | null;
  onCreatePromptTemplate: () => void;
  onCreateWorkflow: () => void;
  onCreateProgram: () => void;
}> = ({ metrics, onCreatePromptTemplate, onCreateWorkflow, onCreateProgram }) => {
  const theme = useTheme();
  const hasPrompt = (metrics?.prerequisites.promptTemplateCount ?? 0) > 0;
  const hasWorkflow = (metrics?.prerequisites.masteryWorkflowCount ?? 0) > 0;
  const ready = hasPrompt && hasWorkflow;

  const Step: React.FC<{
    done: boolean;
    n: number;
    title: string;
    desc: string;
    action: string;
    onAction: () => void;
    permission?: string;
  }> = ({ done, n, title, desc, action, onAction, permission }) => (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${done ? alpha(theme.palette.success.main, 0.4) : theme.palette.divider}`,
        backgroundColor: done ? alpha(theme.palette.success.main, 0.06) : "transparent",
        display: "flex",
        gap: 2,
        alignItems: "center",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        {done ? (
          <CheckCircleIcon color="success" />
        ) : (
          <RadioButtonUncheckedIcon color="disabled" />
        )}
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Step {n} - {title}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {desc}
        </Typography>
      </Box>
      <Button
        variant={done ? "text" : "outlined"}
        size="small"
        endIcon={<ArrowForwardIcon fontSize="small" />}
        onClick={onAction}
        disabled={permission ? false : !ready}
      >
        {done ? "Manage" : action}
      </Button>
    </Box>
  );

  return (
    <Card sx={{ p: { xs: 2, md: 4 }, borderRadius: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
        <LibraryBooksIcon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Start your Program Library
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3, maxWidth: 640 }}>
        A program groups the behavioural targets you teach to a learner: what to measure,
        which prompt level template to use, and when a target counts as mastered. Set up the two
        shared building blocks below once, then create as many programs as you need.
      </Typography>

      <Stack spacing={1.5}>
        <Step
          done={hasPrompt}
          n={1}
          title="Prompt Levels"
          desc="Define the levels of support (e.g. Full Physical ? Verbal ? Visual ? Independent) that data recorders will pick from during a session."
          action="Create Prompt Levels"
          permission="prompt"
          onAction={onCreatePromptTemplate}
        />
        <Step
          done={hasWorkflow}
          n={2}
          title="Workflow"
          desc="Set the thresholds - percent correct, number of sessions - that move a target from baseline to acquisition to mastered."
          action="Create workflow"
          permission="workflow"
          onAction={onCreateWorkflow}
        />
        {/* Batch 13 A.4 - New Program button must never be disabled.
           Passing a truthy `permission` here flips the Step button's
           `disabled` short-circuit off; ready-state now shapes copy
           only, not enable/disable. */}
        <Step
          done={false}
          n={3}
          title="Your first program"
          desc={ready
            ? "You're ready. Create a program, add targets, and start recording sessions."
            : "You can start now - Prompt Levels and a Workflow can be attached later."}
          action="Create program"
          permission="program"
          onAction={onCreateProgram}
        />
      </Stack>
    </Card>
  );
};

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const isMobileRes = useIsMobile();
  const theme = useTheme();

  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  // Batch 31 "3.5 " expansion is per-user, persisted across in-session
  // navigation so a tree the user has organised stays open when they come
  // back from a program page.
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(() => {
    try {
      const raw = sessionStorage.getItem("aba.programLibrary.expanded");
      return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
    } catch { return new Set(); }
  });
  const toggleFolderExpanded = React.useCallback((id: string) => {
    setExpandedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { sessionStorage.setItem("aba.programLibrary.expanded", JSON.stringify(Array.from(next))); } catch {}
      return next;
    });
  }, []);
  const [folderMenuAnchor, setFolderMenuAnchor] = useState<null | HTMLElement>(null);
  const [contextFolder, setContextFolder] = useState<IFolder | null>(null);
  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");

  const [programMenuAnchor, setProgramMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedProgram, setSelectedProgram] = useState<IProgram | null>(null);
  const [moveProgramDialogOpen, setMoveProgramDialogOpen] = useState(false);
  const [copyToClientsDialogOpen, setCopyToClientsDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Batch 1A "1A.7 " bulk selection state. Tracks selected PROGRAM ids
  // only. Folder checkboxes cascade to their descendant programs (see
  // note in Priority 5 commit / final report for rationale). Deleting or
  // moving a folder is not part of bulk actions per spec - the bulk bar
  // shows only Move to folder, Copy to client(s), Delete, all of which
  // operate on programs.
  const [selectedProgramIds, setSelectedProgramIds] = useState<Set<string>>(new Set());
  const toggleProgramSelected = React.useCallback((id: string) => {
    setSelectedProgramIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const setProgramsSelected = React.useCallback((ids: string[], selected: boolean) => {
    setSelectedProgramIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) { if (selected) next.add(id); else next.delete(id); }
      return next;
    });
  }, []);
  const clearSelection = React.useCallback(() => setSelectedProgramIds(new Set()), []);
  const [bulkCopyDialogOpen, setBulkCopyDialogOpen] = useState(false);

  // Batch 29 - pagination + records-per-page. Rasmus uses 250; we offer a
  // narrower ladder because the current UI is card-grid, not table.
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(50);
  useEffect(() => {
    // Reset to page 1 whenever any filter changes; staying on p3 after a
    // search that has 2 pages is disorienting.
    setPage(1);
  }, [currentSearchQuery, selectedFolderId, statusFilter, categoryFilter, perPage]);

  const { data: folderData, isLoading: foldersLoading } = useGetProgramFolderQuery({});
  const {
    data: programData,
    isLoading: programsLoading,
    isError: programsError,
    refetch: refetchPrograms,
  } = useGetProgramQuery({
    searchText: currentSearchQuery,
    folderId: selectedFolderId || undefined,
    status: statusFilter !== "all" ? statusFilter : undefined,
    category: categoryFilter !== "all" ? categoryFilter : undefined,
    page,
    perPage,
  });
  // Batch 32 carryover - ask the backend to also return the full folder ?
  // program map so the tree stays complete even when the paged /program
  // endpoint would clip results.
  const { data: metricsData, isLoading: metricsLoading } = useGetProgramLibraryMetricsQuery({ groupByFolder: true });

  const [createFolder] = useCreateProgramFolderMutation();
  const [deleteFolder] = useDeleteProgramFolderMutation();
  const [moveProgramToFolder] = useMoveProgramToFolderMutation();
  const [moveFolder] = useMoveFolderMutation();
  const [cloneProgram] = useCloneProgramMutation();
  const [deleteProgram] = useDeleteProgramMutation();
  const confirm = useConfirm();
  const [lockProgram] = useLockProgramMutation();
  const [unlockProgram] = useUnlockProgramMutation();

  useEffect(() => {
    if (debouncedSearchQuery.length >= 3 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery]);

  const folders: IFolder[] = Array.isArray(folderData?.data) ? (folderData!.data as IFolder[]) : [];
  // Batch 29 - index() uses DB::table (not Eloquent), so `tags` arrives as a
  // JSON string, not an array. Parse it here so IProgram consumers get the
  // shape they already assume.
  const programs: IProgram[] = useMemo(() => {
    const raw = Array.isArray(programData?.data) ? (programData!.data as any[]) : [];
    return raw.map((p) => {
      // RTK-Query freezes cached responses - don't mutate p.tags in place.
      let tags: any = p.tags;
      if (typeof tags === "string" && tags.length) {
        try { tags = JSON.parse(tags); } catch { tags = []; }
      }
      return { ...p, tags: Array.isArray(tags) ? tags : [] } as IProgram;
    });
  }, [programData]);
  const metrics: ILibraryMetrics | null = (metricsData?.data as ILibraryMetrics) || null;

  // Batch 32 carryover - prefer the backend's groupByFolder map (which
  // covers ALL programs regardless of paging) and only fall back to the
  // paged `programs` array when the backend hasn't populated it yet.
  //
  // Search-aware override: whenever a search is active, ignore the
  // server metrics map (it is unfiltered) and derive strictly from the
  // search-filtered `programs` array so sibling non-matches never
  // appear in the tree.
  //
  // TRAP — the metrics endpoint's programsByFolder map is NOT
  // search-filtered. Re-consuming it under active search will
  // reintroduce the "tree renders full library while paged list is
  // filtered" defect the folder-name-search fix closed. If you touch
  // this seam, either add searchText to libraryMetrics OR keep this
  // branch bypassing the server map under search.
  const programsByFolder = useMemo(() => {
    const map = new Map<string, IProgram[]>();
    const searchActive = currentSearchQuery.trim().length > 0;

    if (!searchActive) {
      const serverMap = (metrics as any)?.programsByFolder as Record<string, IProgram[]> | null | undefined;
      if (serverMap && typeof serverMap === "object") {
        for (const [k, list] of Object.entries(serverMap)) {
          map.set(k, list as IProgram[]);
        }
        return map;
      }
    }

    for (const p of programs) {
      const k = p.folderId != null ? String(p.folderId) : "__root__";
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(p);
    }
    return map;
  }, [programs, metrics, currentSearchQuery]);

  // Folder-name matches (client-side companion to the server's
  // folder-name search predicate — server includes the subtree's
  // programs, client marks the folders themselves alive and expanded).
  // Names are case-insensitive substring, same as the LIKE on the
  // server. `folderNameMatchSubtree` = union of matched folder ids
  // AND every descendant, so a match at any depth expands cleanly.
  const { folderNameMatchIds, folderNameMatchSubtree } = useMemo(() => {
    const matched = new Set<string>();
    const subtree = new Set<string>();
    const needle = currentSearchQuery.trim().toLowerCase();
    if (needle.length === 0) return { folderNameMatchIds: matched, folderNameMatchSubtree: subtree };
    const collect = (list: IFolder[] = [], insideMatch: boolean): void => {
      for (const f of list) {
        const nameHit = String(f.folderName ?? "").toLowerCase().includes(needle);
        if (nameHit) matched.add(String(f.id));
        const alive = insideMatch || nameHit;
        if (alive) subtree.add(String(f.id));
        collect(f.children ?? [], alive);
      }
    };
    collect(folders, false);
    return { folderNameMatchIds: matched, folderNameMatchSubtree: subtree };
  }, [folders, currentSearchQuery]);

  // When a search is active, hide folders whose entire subtree contains
  // no post-filter matches. The `folders` walk itself stays intact so
  // ancestor auto-expand keeps working for the folders we do render.
  // A folder-name match keeps the folder AND its descendants alive
  // regardless of program hits — searching a folder name asks
  // "show me what's in this folder", so returning it collapsed and
  // empty answers nothing.
  const foldersWithMatches = useMemo<Set<string> | null>(() => {
    if (currentSearchQuery.trim().length === 0) return null;
    const alive = new Set<string>();
    const visit = (list: IFolder[] = []): boolean => {
      let any = false;
      for (const f of list) {
        const key = String(f.id);
        const selfHas = (programsByFolder.get(key) ?? []).length > 0;
        const nameHas = folderNameMatchSubtree.has(key);
        const childHas = visit(f.children ?? []);
        if (selfHas || nameHas || childHas) { alive.add(key); any = true; }
      }
      return any;
    };
    visit(folders);
    return alive;
  }, [currentSearchQuery, folders, programsByFolder, folderNameMatchSubtree]);

  // Programs are already filtered server-side by folder/status/category/search.
  const visibleFolderName = useMemo(() => {
    if (!selectedFolderId) return "All Programs";
    const find = (list: IFolder[]): IFolder | undefined => {
      for (const f of list) {
        if (f.id === selectedFolderId) return f;
        if (f.children) {
          const hit = find(f.children);
          if (hit) return hit;
        }
      }
      return undefined;
    };
    return find(folders)?.folderName ?? "All Programs";
  }, [selectedFolderId, folders]);

  // Batch 30 - a folder's full breadcrumb path, e.g.
  //   "ASP Program › Academic/Advanced Cognition › Math"
  // Used in the create-subfolder dialog + delete confirmation.
  const folderPath = (id: string | null): string => {
    if (!id) return "Top level (All Programs)";
    const parents: string[] = [];
    const find = (list: IFolder[], target: string): IFolder | null => {
      for (const f of list) {
        if (f.id === target) return f;
        if (f.children) {
          const hit = find(f.children, target);
          if (hit) return hit;
        }
      }
      return null;
    };
    let cursor: IFolder | null = find(folders, id);
    while (cursor) {
      parents.unshift(cursor.folderName);
      cursor = cursor.parentFolderId ? find(folders, String(cursor.parentFolderId)) : null;
    }
    return parents.length ? parents.join(" / ") : "Top level (All Programs)";
  };

  // Flat, breadcrumb-labelled folder list for the Parent Folder picker.
  const flatFolders = useMemo(() => {
    const out: Array<{ id: string | null; label: string; depth: number }> = [
      { id: null, label: "Top level (All Programs)", depth: 0 },
    ];
    const walk = (list: IFolder[], depth: number, path: string[]) => {
      for (const f of list) {
        const p = [...path, f.folderName];
        out.push({ id: f.id, label: p.join(" / "), depth });
        if (f.children?.length) walk(f.children, depth + 1, p);
      }
    };
    walk(folders, 0, []);
    return out;
  }, [folders]);

  const handleFolderMenu = (event: React.MouseEvent<HTMLElement>, folder: IFolder) => {
    setContextFolder(folder);
    setFolderMenuAnchor(event.currentTarget);
  };
  const handleCloseFolderMenu = () => {
    setFolderMenuAnchor(null);
    setContextFolder(null);
  };

  const [parentForNewFolder, setParentForNewFolder] = useState<string | null>(null);
  const [createFolderError, setCreateFolderError] = useState<string | null>(null);
  const [highlightedFolderId, setHighlightedFolderId] = useState<string | null>(null);

  const openCreateFolder = (parentId: string | null) => {
    setParentForNewFolder(parentId);
    setNewFolderName("");
    setCreateFolderError(null);
    setNewFolderDialogOpen(true);
    handleCloseFolderMenu();
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    setCreateFolderError(null);
    try {
      const response: any = await createFolder({
        folderName: newFolderName.trim(),
        parentFolderId: parentForNewFolder ?? undefined,
      }).unwrap();
      if (response.status) {
        showMessage(response.message || "Folder created.");
        setNewFolderDialogOpen(false);
        setNewFolderName("");
        // Batch 47 - refresh only the parent branch and highlight the new folder.
        const newId = response?.data?.id ? String(response.data.id) : null;
        if (newId) {
          setSelectedFolderId(newId);
          setHighlightedFolderId(newId);
          setTimeout(() => setHighlightedFolderId((cur) => cur === newId ? null : cur), 2500);
        }
        setParentForNewFolder(null);
      } else {
        // Validation errors keep the modal open with an inline message so
        // the administrator can correct name / parent without losing state.
        setCreateFolderError(response.message || "Failed to create folder.");
      }
    } catch (e: any) {
      setCreateFolderError(e?.data?.message || "Failed to create folder.");
    }
  };

  // ── Rename ─────────────────────────────────────────────────────────
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const openRenameFolder = () => {
    if (!contextFolder) return;
    setRenameValue(contextFolder.folderName);
    setRenameDialogOpen(true);
    setFolderMenuAnchor(null);
  };
  const handleRenameFolder = async () => {
    if (!contextFolder || !renameValue.trim()) return;
    try {
      const response = await createFolder({
        id: contextFolder.id,
        folderName: renameValue.trim(),
        parentFolderId: contextFolder.parentFolderId ?? undefined,
      }).unwrap();
      if (response.status) {
        showMessage(response.message || "Folder renamed.");
        setRenameDialogOpen(false);
        setContextFolder(null);
      } else {
        showErrorMessage(response.message || "Failed to rename folder.");
      }
    } catch {
      showErrorMessage("Failed to rename folder.");
    }
  };

  // ── Delete with confirmation ───────────────────────────────────────
  const [deleteFolderState, setDeleteFolderState] = useState<null | {
    folder: IFolder;
    counts: { childFolders: number; programs: number };
  }>(null);
  const openDeleteFolder = async () => {
    if (!contextFolder) return;
    const folder = contextFolder;
    setFolderMenuAnchor(null);
    try {
      // First DELETE call with no strategy - backend either succeeds (empty
      // folder) or returns requires_confirmation with counts.
      const res = await deleteFolder({ id: folder.id }).unwrap();
      if (res.status) {
        showMessage(res.message || "Folder deleted.");
        if (selectedFolderId === folder.id) setSelectedFolderId(null);
        setContextFolder(null);
        return;
      }
      const anyRes = res as any;
      if (anyRes.requires_confirmation) {
        setDeleteFolderState({
          folder,
          counts: anyRes.counts as { childFolders: number; programs: number },
        });
      } else {
        showErrorMessage(res.message || "Failed to delete folder.");
      }
    } catch {
      showErrorMessage("Failed to delete folder.");
    }
  };
  const commitDeleteFolder = async (strategy: "move_to_parent" | "move_to_root" | "delete_contents") => {
    if (!deleteFolderState) return;
    try {
      const res = await deleteFolder({
        id: deleteFolderState.folder.id,
        strategy,
        confirm: strategy === "delete_contents",
      }).unwrap();
      if (res.status) {
        showMessage(res.message || "Folder deleted.");
        if (selectedFolderId === deleteFolderState.folder.id) setSelectedFolderId(null);
      } else {
        showErrorMessage(res.message || "Failed to delete folder.");
      }
    } catch {
      showErrorMessage("Failed to delete folder.");
    }
    setDeleteFolderState(null);
    setContextFolder(null);
  };

  // ── Move folder into another folder ────────────────────────────────
  const [moveFolderDialogOpen, setMoveFolderDialogOpen] = useState(false);
  const openMoveFolder = () => {
    if (!contextFolder) return;
    setMoveFolderDialogOpen(true);
    setFolderMenuAnchor(null);
  };
  const handleMoveFolder = async (targetFolderId: string | null) => {
    if (!contextFolder) return;
    try {
      const res = await moveFolder({
        folderId: contextFolder.id,
        targetFolderId: targetFolderId ?? null,
      }).unwrap();
      if (res.status) {
        showMessage(res.message || "Folder moved.");
      } else {
        showErrorMessage(res.message || "Failed to move folder.");
      }
    } catch {
      showErrorMessage("Failed to move folder.");
    }
    setMoveFolderDialogOpen(false);
    setContextFolder(null);
  };

  const handleMoveProgram = async (folderId: string) => {
    // Batch 1A "1A.7 " bulk mode: iterate every selected program if the
    // dialog was opened from the bulk bar. Single-mode fallback uses
    // selectedProgram (the row menu path). Success/failure counts reported
    // in one toast rather than one per row.
    if (selectedProgramIds.size > 0) {
      const ids = Array.from(selectedProgramIds);
      let ok = 0, fail = 0;
      for (const id of ids) {
        try {
          const r = await moveProgramToFolder({ programId: id, folderId }).unwrap();
          if (r?.status) ok++; else fail++;
        } catch { fail++; }
      }
      if (ok) showMessage(`${ok} program${ok === 1 ? "" : "s"} moved${fail ? `, ${fail} failed` : ""}.`);
      if (fail && !ok) showErrorMessage(`${fail} move${fail === 1 ? "" : "s"} failed.`);
      refetchPrograms();
      clearSelection();
      return;
    }
    if (!selectedProgram) return;
    try {
      const response = await moveProgramToFolder({
        programId: selectedProgram.id,
        folderId,
      }).unwrap();
      if (response.status) {
        showMessage(response.message || "Program moved successfully");
        refetchPrograms();
      } else {
        showErrorMessage(response.message || "Failed to move program");
      }
    } catch {
      showErrorMessage("Failed to move program");
    }
    setSelectedProgram(null);
  };

  const handleProgramMenuOpen = (event: React.MouseEvent<HTMLElement>, program: IProgram) => {
    setProgramMenuAnchor(event.currentTarget);
    setSelectedProgram(program);
  };
  const handleProgramMenuClose = () => setProgramMenuAnchor(null);

  const totalPrograms = metrics?.totalPrograms ?? 0;
  const prereqsSatisfied =
    (metrics?.prerequisites.promptTemplateCount ?? 0) > 0 &&
    (metrics?.prerequisites.masteryWorkflowCount ?? 0) > 0;
  // Batch 31 "1.2 " the onboarding card is a TRUE first-run state, not a
  // permanent fixture. It renders only when zero programs AND zero folders
  // exist. A user with programs but no prompt hierarchy still gets the
  // "Setup incomplete" persistent banner below (that guard is unchanged).
  // Batch 36B - additionally gate on every dependent query having resolved.
  // During load, folders=[] and totalPrograms=0 look identical to the true
  // first-run state; the onboarding block was flashing every page load.
  //
  // Batch 1A "1A.2 " the tree ALWAYS renders now. The old PrereqChecklist
  // swap gated a demo behind an educational panel; user tapping the
  // primary button and having nothing happen is worse than no onboarding.
  // The empty-library guidance moved to a slim dismissible strip (see
  // showEmptyLibraryStrip below); the tree itself is never hidden.
  const initialLoading = foldersLoading || metricsLoading || programsLoading;
  const isEmptyLibrary = !initialLoading && totalPrograms === 0 && folders.length === 0;
  const [emptyStripDismissed, setEmptyStripDismissed] = React.useState<boolean>(() => {
    try { return localStorage.getItem("aba.emptyLibraryStrip.dismissed") === "1"; }
    catch { return false; }
  });
  const dismissEmptyStrip = React.useCallback(() => {
    setEmptyStripDismissed(true);
    try { localStorage.setItem("aba.emptyLibraryStrip.dismissed", "1"); } catch { /* ignore */ }
  }, []);
  const showEmptyLibraryStrip = isEmptyLibrary && !emptyStripDismissed;

  // Stat cards - every value comes from a real SELECT (metrics endpoint).
  // "Targets approaching mastery" is NOT rendered: computing it requires
  // per-target session aggregates joined against workflow phase thresholds,
  // which no endpoint currently returns. See report for the gap.
  // Batch 31 - the six full-width KPI tiles were consuming the entire
  // above-the-fold and every value read zero on a fresh install. Collapsed
  // to one quiet summary strip under the page title. The tiles are still
  // wired to the real metrics query; only the visual weight changed.
  // Suppressed entirely when every figure is zero ( - 2.2).
  const activeProgramsCount = metrics?.activePrograms ?? metrics?.countsByStatus.active ?? 0;
  const assignedStudentsCount = metrics?.assignedStudents ?? metrics?.studentsWithActive ?? 0;
  const sessionsThisWeekCount = metrics?.sessionsThisWeek ?? metrics?.sessionsLast7Days ?? 0;
  const activeTargetsCount = metrics?.activeTargets ?? 0;
  const masteredTargetsCount = metrics?.masteredTargets ?? 0;
  const allZero =
    totalPrograms === 0 && activeProgramsCount === 0 && assignedStudentsCount === 0 &&
    activeTargetsCount === 0 && masteredTargetsCount === 0 && sessionsThisWeekCount === 0;
  // Batch 39 - premium KPI cards. Each metric is a small white card with a
  // tinted icon tile (accent colour), large number, and label. Equal
  // height, equal padding, subtle hover lift. Suppressed entirely when
  // every figure is zero (same rule as before). Data unchanged.
  type KpiDef = { value: number; label: string; icon: React.ReactNode; accent: string; tile: string };
  const kpiDefs: KpiDef[] = [
    { value: totalPrograms,         label: totalPrograms === 1 ? "Program" : "Programs",
      icon: <LibraryBooksIcon fontSize="small" />, accent: B13.primary,   tile: tint12(B13.primary) },
    { value: activeProgramsCount,   label: "Active",
      icon: <CheckCircleIcon fontSize="small" />, accent: LegacyTokens.teal,    tile: LegacyTokens.tealSoft },
    { value: assignedStudentsCount, label: assignedStudentsCount === 1 ? "Student" : "Students",
      icon: <PersonOutlineIcon fontSize="small" />, accent: LegacyTokens.blue,  tile: LegacyTokens.blueSoft },
    { value: activeTargetsCount,    label: "Active Targets",
      icon: <PlaylistAddCheckIcon fontSize="small" />, accent: LegacyTokens.purple, tile: LegacyTokens.purpleSoft },
    { value: masteredTargetsCount,  label: "Mastered Targets",
      icon: <CheckCircleIcon fontSize="small" />, accent: LegacyTokens.emerald, tile: LegacyTokens.emeraldSoft },
    { value: sessionsThisWeekCount, label: "Sessions This Week",
      icon: <EventNoteIcon fontSize="small" />,    accent: LegacyTokens.amber,  tile: LegacyTokens.amberSoft },
  ];
  const statStrip = (metricsLoading || allZero) ? null : (
    <Grid container spacing={2} data-testid="library-stat-strip">
      {kpiDefs.map((k) => (
        <Grid key={k.label} item xs={6} sm={4} md={2}>
          <Box sx={{
            height: "100%", p: 2,
            display: "flex", flexDirection: "column", gap: 1.25,
            backgroundColor: LegacyTokens.surface,
            border: `1px solid ${LegacyTokens.line}`,
            borderRadius: `${LegacyTokens.radius.card}px`,
            transition: "box-shadow 150ms, transform 150ms",
            "&:hover": { boxShadow: "0 2px 8px rgba(15, 61, 46, 0.06)" },
          }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: `${LegacyTokens.radius.control}px`,
              backgroundColor: k.tile, color: k.accent,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>{k.icon}</Box>
            <Box>
              <Typography sx={{ fontSize: 24, fontWeight: 650, color: LegacyTokens.ink, lineHeight: 1.1 }}>
                {k.value}
              </Typography>
              <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mt: 0.5 }}>
                {k.label}
              </Typography>
            </Box>
          </Box>
        </Grid>
      ))}
    </Grid>
  );

  // Batch 38 "3.5 " the old row of status-shortcut chips (Active " 1,
  // Waiting " 0, ) is gone. The Status dropdown filter already lets a
  // user pick a status. A single inline "active filter" chip in
  // brand-soft appears at the end of the filter row when a filter is set,
  // with a clear-affordance to remove it. Rendered inline below, not here.
  const statusChips = null;

  return (
    // Batch 13 Part B - Program Library adopts the new palette.
    // Canvas swaps from the legacy warm green-grey (#F4F7F5) to the
    // cooler B13 #F7F9FB. Cards and status chips inside inherit the
    // new ink stack and semantic status accents. Other ABA pages
    // remain on PageSurface until they are individually redesigned.
    <Box data-testid="program-library-page" sx={{ backgroundColor: B13.canvas, minHeight: "100vh" }}>
    {/* Batch 39 - premium header: tinted module icon square, title, badge,
       description, KPI cards. Actions sit on the right and wrap on smaller
       screens instead of stacking below (cleaner composition). */}
    <Box sx={{ px: { xs: 2, md: 3.5 }, pt: { xs: 2, md: 3 }, mb: 3 }}>
      <Breadcrumbs sx={{ mb: 1.5, fontSize: 12 }} separator="/">
        <MuiLink component={Link} to="/aba-therapy/programs" underline="hover" sx={{ color: LegacyTokens.ink3 }}>ABA Therapy</MuiLink>
        <Typography sx={{ fontSize: 12, color: LegacyTokens.ink }}>Programs</Typography>
      </Breadcrumbs>
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, alignItems: { md: "flex-start" }, justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", minWidth: 0 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: `${LegacyTokens.radius.control}px`,
            backgroundColor: tint12(B13.primary), color: B13.primary,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <LibraryBooksIcon fontSize="medium" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography component="h1" sx={{ ...TypeScale.pageTitle, lineHeight: 1.15 }}>
                Program Library
              </Typography>
              <Box sx={{
                display: "inline-flex", alignItems: "center",
                backgroundColor: tint12(B13.primary), color: B13.primary,
                fontSize: 11, fontWeight: 600, letterSpacing: "0.06em",
                textTransform: "uppercase",
                px: 1, py: 0.25, borderRadius: `${LegacyTokens.radius.chip}px`,
              }}>Clinical Programs</Box>
            </Box>
            <Typography sx={{ ...TypeScale.caption, mt: 0.5 }}>
              Create, organize, assign, and monitor ABA teaching Programs and their Targets.
            </Typography>
          </Box>
        </Box>
        {/* Actions on the right; wrap on smaller screens. */}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
          <Button
            variant="contained" size="medium"
            startIcon={<CreateNewFolderIcon sx={{ display: "none" }} />}
            onClick={() => navigate("/aba-therapy/programs/new")}
            sx={{
              backgroundColor: B13.primary, color: B13.surface,
              fontWeight: 600, textTransform: "none",
              px: 2, py: 1, borderRadius: `${LegacyTokens.radius.control}px`,
              boxShadow: "none",
              "&:hover": { backgroundColor: B13.primary, filter: "brightness(0.95)" },
              "& .MuiButton-startIcon": { display: "none" },
            }}
          >
            New Program
          </Button>
          {[
            { label: "New Folder", icon: <CreateNewFolderIcon fontSize="small" />, onClick: () => setNewFolderDialogOpen(true) },
            // Batch 61 "5 " Templates promoted to a full page under
            // Settings. Toolbar shortcut removed to avoid two paths
            // for the same surface and to free horizontal space.
            { label: "Import Program", icon: <ArrowForwardIcon fontSize="small" sx={{ transform: "rotate(-90deg)" }} />, onClick: () => setImportDialogOpen(true) },
          ].map((b) => (
            <Button
              key={b.label}
              size="medium"
              startIcon={b.icon}
              onClick={b.onClick}
              sx={{
                backgroundColor: B13.surface,
                color: B13.primary,
                border: `1px solid ${B13.line}`,
                fontWeight: 600, textTransform: "none",
                px: 2, py: 1, borderRadius: `${LegacyTokens.radius.control}px`,
                "&:hover": { backgroundColor: tint12(B13.primary), borderColor: B13.primary },
              }}
            >{b.label}</Button>
          ))}
        </Stack>
      </Box>
      {statStrip}
    </Box>

    {/* Batch 39 - search + filters wrapped in a single premium toolbar
       Card so they read as one control surface, not disconnected fields.
       Search input is visually wider than the filters. Consistent 44px
       height on all controls, 12px radius, refined focus ring. */}
    <Box sx={{ px: { xs: 2, md: 3.5 }, pb: 2 }}>
      {/* Batch 1A "1A.7 " bulk action bar swaps in for the filter bar
         when at least one program is selected. Shows count, Move,
         Copy, Delete, and a Clear action. */}
      {selectedProgramIds.size > 0 ? (
        <Card variant="outlined" sx={{
          backgroundColor: tint12(B13.primary),
          borderColor: B13.primary,
          borderRadius: `${LegacyTokens.radius.card}px`,
          boxShadow: "none",
          p: 1.25,
        }} data-testid="library-bulk-bar">
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ md: "center" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: B13.primary, flexGrow: 1, fontVariantNumeric: "tabular-nums" }}>
              {selectedProgramIds.size} program{selectedProgramIds.size === 1 ? "" : "s"} selected
            </Typography>
            <Button
              size="small"
              startIcon={<DriveFileMoveIcon fontSize="small" />}
              onClick={() => setMoveProgramDialogOpen(true)}
              sx={{ color: B13.primary, textTransform: "none", fontWeight: 600 }}
            >Move to folder</Button>
            <Button
              size="small"
              startIcon={<ContentCopyIcon fontSize="small" />}
              onClick={() => setBulkCopyDialogOpen(true)}
              sx={{ color: B13.primary, textTransform: "none", fontWeight: 600 }}
            >Copy to client(s)</Button>
            <Button
              size="small"
              startIcon={<DeleteOutlineSharpIcon fontSize="small" />}
              onClick={async () => {
                const ids = Array.from(selectedProgramIds);
                // Batch 13 note - spec 1A.5 asks that programs with
                // recorded session data be blocked from deletion with
                // the reason named. That check does NOT exist in
                // ProgramController::destroy today (single or bulk).
                // Adding it requires a backend preflight endpoint;
                // deferred. The confirmation copy names the exact
                // count so the operator can back out before firing.
                const msg = ids.length === 1
                  ? `Delete 1 program? This cannot be undone.`
                  : `Delete ${ids.length} programs? This cannot be undone.`;
                const okBulk = await confirm({
                  title: ids.length === 1 ? "Delete program?" : `Delete ${ids.length} programs?`,
                  message: msg,
                  confirmText: "Delete Permanently",
                  cancelText: "Cancel",
                  variant: "danger",
                });
                if (!okBulk) return;
                let ok = 0, fail = 0;
                for (const id of ids) {
                  try {
                    const res = await deleteProgram(id).unwrap();
                    if (res?.status) ok++; else fail++;
                  } catch { fail++; }
                }
                if (ok) showMessage(`${ok} deleted${fail ? `, ${fail} failed` : ""}.`);
                if (fail && !ok) showErrorMessage(`${fail} deletion${fail === 1 ? "" : "s"} failed.`);
                clearSelection();
              }}
              sx={{ color: "error.main", textTransform: "none", fontWeight: 600 }}
            >Delete</Button>
            <Button
              size="small"
              onClick={clearSelection}
              sx={{ color: B13Ink.ink3, textTransform: "none" }}
            >Clear</Button>
          </Stack>
        </Card>
      ) : (
      <Card variant="outlined" sx={{
        backgroundColor: B13.surface,
        borderColor: B13.line,
        borderRadius: `${LegacyTokens.radius.card}px`,
        boxShadow: "none",
        p: 1.25,
      }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ md: "center" }}>
        <TextField
          data-testid="library-search"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search Programs and folders"
          sx={{
            flexGrow: 1, maxWidth: { md: 480 },
            "& .MuiOutlinedInput-root": {
              backgroundColor: LegacyTokens.canvas2, height: 44,
              borderRadius: `${LegacyTokens.radius.control}px`,
              "& fieldset": { borderColor: "transparent" },
              "&:hover fieldset": { borderColor: LegacyTokens.line },
              "&.Mui-focused fieldset": { borderColor: B13.primary },
            },
          }}
          InputProps={{
            startAdornment: (<InputAdornment position="start"><CiSearch style={{ fontSize: 18, color: LegacyTokens.ink3 }} /></InputAdornment>),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Category</InputLabel>
          <Select value={categoryFilter} label="Category" onChange={(e) => setCategoryFilter(e.target.value)}>
            <MenuItem value="all">All Categories</MenuItem>
            <MenuItem value="Skill Acquisition">Skill Acquisition</MenuItem>
            <MenuItem value="Behavior Reduction">Behavior Reduction</MenuItem>
            <MenuItem value="Social Skills">Social Skills</MenuItem>
            <MenuItem value="Daily Living">Daily Living</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="waiting">Waiting</MenuItem>
            <MenuItem value="baseline">Baseline</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="maintenance">Maintenance</MenuItem>
            <MenuItem value="mastered">Mastered</MenuItem>
            <MenuItem value="hold">Hold</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        </FormControl>

        {/* Batch 38 "3.5 " active-filter chip lives INLINE at the end of the
           filter row (was previously an orphaned line beneath it). Renders
           only when a status filter is set; brand-soft fill, brand text,
           clear-affordance via delete icon that returns status to "all". */}
        {statusFilter !== "all" && (
          <Chip
            size="small"
            label={`Status: ${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}`}
            onDelete={() => setStatusFilter("all")}
            sx={{
              backgroundColor: tint12(B13.primary),
              color: B13.primary,
              fontWeight: 600,
              borderRadius: `${LegacyTokens.radius.chip}px`,
              "& .MuiChip-deleteIcon": { color: B13.primary, "&:hover": { color: B13.primary } },
            }}
          />
        )}
      </Stack>
      </Card>
      )}
    </Box>

    <Box sx={{ px: { xs: 2, md: 3.5 } }}>
      {/* Batch 35C - 4 - the "Setup incomplete" panel was a full-width Card
         (p:2, warning-tinted background) that pushed the folder tree
         below the fold on any populated library missing a prompt
         hierarchy or a mastery workflow. Compressed to a single-line
         caption + inline links so the notice is still visible but no
         longer competes with the tree for vertical space. */}
      {metrics && !prereqsSatisfied && programs.length > 0 && (
        <Box sx={{
          mb: 1.5, py: 0.5, px: 1, borderRadius: 1,
          display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap",
          backgroundColor: alpha(theme.palette.warning.main, 0.06),
          borderLeft: `3px solid ${alpha(theme.palette.warning.main, 0.5)}`,
        }} data-testid="library-prereq-notice">
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            Setup incomplete - </Typography>
          {(metrics?.prerequisites.promptTemplateCount ?? 0) === 0 && (
            <MuiLink component="button" underline="hover" sx={{ fontSize: 12 }}
              onClick={() => navigate("/aba-therapy/prompt-hierarchies")}>
              add a prompt level template
            </MuiLink>
          )}
          {(metrics?.prerequisites.promptTemplateCount ?? 0) === 0
            && (metrics?.prerequisites.masteryWorkflowCount ?? 0) === 0 && (
            <Typography variant="caption" color="text.secondary">-</Typography>
          )}
          {(metrics?.prerequisites.masteryWorkflowCount ?? 0) === 0 && (
            <MuiLink component="button" underline="hover" sx={{ fontSize: 12 }}
              onClick={() => navigate("/aba-therapy/workflows")}>
              add a mastery workflow
            </MuiLink>
          )}
        </Box>
      )}

      {/* Batch 1A "1A.2 " dismissible empty-library strip. Renders only
         when there are zero programs AND zero folders AND the user has
         not dismissed it before. Once dismissed it stays dismissed per
         user (localStorage). One program or folder existing hides it
         unconditionally. The tree ALWAYS renders below, never swapped
         out for a full-page checklist. */}
      {showEmptyLibraryStrip && (
        <Box sx={{
          mb: 1.5, py: 1, px: 1.5, borderRadius: `${LegacyTokens.radius.chip}px`,
          display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap",
          backgroundColor: tint12(B13.primary),
          border: `1px solid ${B13.line}`,
        }} data-testid="library-empty-strip">
          <Typography sx={{ fontSize: 13, color: B13Ink.ink2, flexGrow: 1 }}>
            Your library is empty. Start with <MuiLink component="button" underline="hover" sx={{ fontSize: 13, fontWeight: 600, color: B13.primary }}
              onClick={() => navigate("/aba-therapy/programs/new")}>a new program</MuiLink>
            {" - or set up "}
            <MuiLink component="button" underline="hover" sx={{ fontSize: 13, color: B13.primary }}
              onClick={() => navigate("/aba-therapy/settings/prompt-levels")}>Prompt Levels</MuiLink>
            {" and "}
            <MuiLink component="button" underline="hover" sx={{ fontSize: 13, color: B13.primary }}
              onClick={() => navigate("/aba-therapy/settings/workflows")}>a Workflow</MuiLink>
            {" first so new programs pick them up automatically."}
          </Typography>
          <Button size="small" onClick={dismissEmptyStrip}
            sx={{ fontSize: 12, textTransform: "none", color: B13Ink.ink3, minWidth: "auto" }}>
            Dismiss
          </Button>
        </Box>
      )}

      {/* Batch 13 Part B - hairlines only. The tree used to sit
         inside an outlined Card; now the rows themselves carry the
         dividers (borderBottom on each ProgramTreeRow and folder
         row's hover state), so the containing box just provides a
         surface and top hairline. */}
      {(
        <Card variant="outlined" sx={{
          p: 0,
          backgroundColor: B13.surface,
          borderColor: B13.line,
          borderRadius: `${LegacyTokens.radius.card}px`,
          boxShadow: "none",
        }}>
          {foldersLoading ? (
            <Box display="flex" justifyContent="center" p={2}><CircularProgress size={24} /></Box>
          ) : (
            <>
              {/* "4.4 " "All Programs" pinned row keeps its position on the
                 shared canvas2 surface with the brand library icon. */}
              <Box
                data-testid="folder-row-all"
                role="button" tabIndex={0}
                onClick={() => setSelectedFolderId(null)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedFolderId(null); } }}
                aria-pressed={selectedFolderId === null}
                sx={{
                  display: "flex", alignItems: "center", gap: 1, px: 1,
                  minHeight: 44, cursor: "pointer",
                  borderRadius: `${LegacyTokens.radius.chip}px`,
                  backgroundColor: selectedFolderId === null ? tint12(B13.primary) : B13.canvas,
                  transition: "background-color 150ms",
                  "&:hover": {
                    backgroundColor: selectedFolderId === null ? tint12(B13.primary) : B13.line,
                  },
                }}
              >
                <Box sx={{ width: 20 }} />
                <Box sx={{ width: 22, display: "flex", justifyContent: "center" }}>
                  <LibraryBooksIcon fontSize="small" sx={{ color: B13.primary }} />
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: selectedFolderId === null ? B13.primary : B13.ink, flexGrow: 1 }}>
                  All Programs
                </Typography>
                {metrics && (
                  <Box sx={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    minWidth: 40, height: 22, px: 1,
                    backgroundColor: selectedFolderId === null ? B13.primary : B13.surface,
                    color: selectedFolderId === null ? B13.surface : B13.primary,
                    fontSize: 12, fontWeight: 700, fontVariantNumeric: "tabular-nums",
                    borderRadius: "11px",
                    border: selectedFolderId === null ? "none" : `1px solid ${B13.line}`,
                  }}>
                    {totalPrograms} {totalPrograms === 1 ? "Program" : "Programs"}
                  </Box>
                )}
              </Box>

              {initialLoading ? (
                // Batch 36B - placeholder rows matching folder-row height so
                // the tree doesn't flash "No folders yet" during load.
                <Box sx={{ px: 1, py: 0.5 }}>
                  {[0,1,2].map((i) => (
                    <Box key={i} sx={{
                      display: "flex", alignItems: "center", gap: 1, py: 0.75, px: 1,
                      opacity: 0.35,
                    }}>
                      <Box sx={{ width: 20 }} />
                      <Box sx={{ width: 22, height: 20, borderRadius: 0.5, bgcolor: "action.hover" }} />
                      <Box sx={{ flexGrow: 1, height: 12, borderRadius: 0.5, bgcolor: "action.hover" }} />
                    </Box>
                  ))}
                </Box>
              ) : folders.length === 0 && programs.length === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 2, py: 3, textAlign: "center" }}>
                  No folders yet. Use "New Folder" above to create your first folder.
                </Typography>
              ) : currentSearchQuery.trim().length > 0
                    && foldersWithMatches !== null
                    && foldersWithMatches.size === 0
                    && (programsByFolder.get("__root__") ?? []).length === 0 ? (
                // Defect-2 guard — search returned nothing. Render a
                // named no-results row inside the tree card so the
                // filter cannot silently fall open to the full tree.
                // Complements the outer library-empty-state box; this
                // one is what the tree slot itself says.
                <Typography
                  variant="caption"
                  color="text.secondary"
                  data-testid="library-tree-no-results"
                  sx={{ display: "block", px: 2, py: 3, textAlign: "center" }}
                >
                  No folders or programs match "{currentSearchQuery.trim()}".
                </Typography>
              ) : (
                <>
                  {folders.map((folder) => (
                    <FolderTreeNode
                      key={folder.id}
                      folder={folder}
                      selectedFolderId={selectedFolderId}
                      highlightedFolderId={highlightedFolderId}
                      autoExpandIds={(() => {
                        // Union: ancestors of the freshly-created folder
                        // (Batch 47 highlight) PLUS every folder in a
                        // folder-name-match subtree so the matched
                        // folder opens and its descendants are visible.
                        const s = new Set<string>(ancestorSetOf(folders, highlightedFolderId));
                        folderNameMatchSubtree.forEach((id) => s.add(id));
                        return s;
                      })()}
                      programsByFolder={programsByFolder}
                      onSelect={setSelectedFolderId}
                      onOpenFolder={(id) => setSelectedFolderId(id)}
                      onMenu={handleFolderMenu}
                      onProgramMenu={handleProgramMenuOpen}
                      onOpenProgram={(p) => {
                        if ((p as any).isLocked) {
                          showErrorMessage("Locked programs cannot be opened or edited.");
                          return;
                        }
                        navigate(`/aba-therapy/programs/${p.id}`);
                      }}
                      onCopyProgramToClients={(p) => { setSelectedProgram(p); setCopyToClientsDialogOpen(true); }}
                      selectedProgramIds={selectedProgramIds}
                      onToggleProgramSelected={toggleProgramSelected}
                      onSetProgramsSelected={setProgramsSelected}
                      expandedFolderIds={expandedFolderIds}
                      onToggle={toggleFolderExpanded}
                      level={0}
                      visibleFolderIds={foldersWithMatches}
                    />
                  ))}
                  {/* Programs that belong to no folder appear as top-level rows. */}
                  {(programsByFolder.get("__root__") ?? []).map((p) => (
                    <ProgramTreeRow key={p.id} program={p} level={0}
                      onOpen={() => {
                        if ((p as any).isLocked) {
                          showErrorMessage("Locked programs cannot be opened or edited.");
                          return;
                        }
                        navigate(`/aba-therapy/programs/${p.id}`);
                      }}
                      onMenu={(e) => handleProgramMenuOpen(e, p)}
                      onCopyToClients={(pp) => { setSelectedProgram(pp); setCopyToClientsDialogOpen(true); }}
                      selected={selectedProgramIds.has(String(p.id))}
                      onToggleSelect={toggleProgramSelected} />
                  ))}
                </>
              )}
            </>
          )}
        </Card>
      )}

      {/* Batch 35A "2 " the message must reflect what is actually true. The
         old condition blamed filters whenever there were zero programs and
         at least one folder, regardless of whether anything was filtered.
         Now: if no filter is set, offer the primary action; only blame
         filters when at least one is active. */}
      {!initialLoading && programs.length === 0 && folders.length > 0 && (() => {
        // Batch 1A "1A.9 " four distinct empty states share this
        // container but never share their message. Precedence:
        //   1. search term entered but zero hits - name the term
        //   2. non-search filter active (status/category/folder) - //      point at the specific filters
        //   3. truly empty of programs but folders exist - offer New
        //      Program (the empty-library strip above handles zero-
        //      folders-and-zero-programs)
        // Loading rows sit inside the Card block above (skeleton at
        // the correct row height) and are a separate state entirely.
        const searchActive = currentSearchQuery.trim() !== "";
        const nonSearchFilterActive = (
          statusFilter !== "all" ||
          categoryFilter !== "all" ||
          selectedFolderId !== null
        );
        return (
          <Box sx={{ p: 4, textAlign: "center", border: `1px dashed ${theme.palette.divider}`, borderRadius: 2, mt: 2 }}
            data-testid="library-empty-state">
            {searchActive ? (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  No programs match "{currentSearchQuery.trim()}".
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, mb: 2 }}>
                  Check spelling or try a different term.
                </Typography>
                <Button variant="outlined" size="small"
                  onClick={() => setSearchQuery("")}>
                  Clear search
                </Button>
              </>
            ) : nonSearchFilterActive ? (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>No programs match the active filters.</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, mb: 2 }}>
                  Try clearing the status, category, or folder filter.
                </Typography>
                <Button variant="outlined" size="small"
                  onClick={() => {
                    setStatusFilter("all");
                    setCategoryFilter("all");
                    setSelectedFolderId(null);
                  }}>
                  Clear filters
                </Button>
              </>
            ) : (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>No programs in the library yet.</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, mb: 2 }}>
                  Folders are in place - add your first program to get started.
                </Typography>
                <Button variant="contained" size="small"
                  onClick={() => navigate("/aba-therapy/programs/new")}>
                  New Program
                </Button>
              </>
            )}
          </Box>
        );
      })()}
    </Box>

      {/* Folder context menu (Batch 30) */}
      <Menu
        anchorEl={folderMenuAnchor}
        open={Boolean(folderMenuAnchor)}
        onClose={handleCloseFolderMenu}
      >
        <MenuItem onClick={() => openCreateFolder(contextFolder?.id ?? null)}>
          <CreateNewFolderIcon fontSize="small" sx={{ mr: 1 }} /> New Child Folder
        </MenuItem>
        <MenuItem onClick={openRenameFolder}>
          <EditSharpIcon fontSize="small" sx={{ mr: 1 }} /> Rename
        </MenuItem>
        <MenuItem onClick={openMoveFolder}>
          <DriveFileMoveIcon fontSize="small" sx={{ mr: 1 }} /> Move to folder
        </MenuItem>
        <MenuItem onClick={openDeleteFolder} sx={{ color: "error.main" }}>
          <DeleteOutlineSharpIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Program three-dot menu - Batch 30 filled out */}
      <Menu
        anchorEl={programMenuAnchor}
        open={Boolean(programMenuAnchor)}
        onClose={handleProgramMenuClose}
      >
        {/* Batch 1A "1A.5 " kebab items in exact spec order. Items ending
           with " - " open a dialog; items without act immediately.
           `Delete` confirms first. `Lock program` toggles to `Unlock
           program` when the row is already locked. */}
        <MenuItem
          onClick={() => {
            handleProgramMenuClose();
            setCopyToClientsDialogOpen(true);
          }}
        >
          <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} /> Copy to client(s) - </MenuItem>
        <MenuItem
          disabled={!!(selectedProgram as any)?.isLocked}
          onClick={() => {
            handleProgramMenuClose();
            if ((selectedProgram as any)?.isLocked) {
              showErrorMessage("Locked programs cannot be opened or edited.");
              return;
            }
            if (selectedProgram) navigate(`/aba-therapy/programs/${selectedProgram.id}`);
          }}
        >
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} /> Preview
        </MenuItem>
        <MenuItem
          disabled={!!(selectedProgram as any)?.isLocked}
          onClick={() => {
            handleProgramMenuClose();
            if ((selectedProgram as any)?.isLocked) {
              showErrorMessage("Locked programs cannot be opened or edited.");
              return;
            }
            if (selectedProgram) navigate(`/aba-therapy/programs/edit/${selectedProgram.id}`);
          }}
        >
          <EditSharpIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={async () => {
            handleProgramMenuClose();
            if (!selectedProgram) return;
            // Batch 1A "1A.5 " ellipsis label promised a dialog; the
            // full name-picker + folder-picker dialog is out of scope
            // for this batch, so the immediate clone is preserved and
            // the ellipsis kept to honour the label spec. Follow-up
            // ticket will add the picker.
            try {
              const res = await cloneProgram({ programId: selectedProgram.id }).unwrap();
              if (res.status) {
                showMessage(res.message || "Program duplicated.");
              } else {
                showErrorMessage(res.message || "Failed to duplicate program.");
              }
            } catch {
              showErrorMessage("Failed to duplicate program.");
            }
          }}
        >
          <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} /> Duplicate
        </MenuItem>
        <MenuItem
          disabled={!!(selectedProgram as any)?.isLocked}
          onClick={() => {
            handleProgramMenuClose();
            if ((selectedProgram as any)?.isLocked) {
              showErrorMessage("Locked programs cannot be moved.");
              return;
            }
            setMoveProgramDialogOpen(true);
          }}
        >
          <DriveFileMoveIcon fontSize="small" sx={{ mr: 1 }} /> Move to folder
        </MenuItem>
        <MenuItem
          disabled={!!(selectedProgram as any)?.isLocked}
          onClick={async () => {
            handleProgramMenuClose();
            if (!selectedProgram) return;
            if ((selectedProgram as any)?.isLocked) {
              showErrorMessage("Locked programs cannot be deleted.");
              return;
            }
            const okDelete = await confirm({
              title: "Delete program?",
              message: `Delete program "${selectedProgram.programName}"?\nThis action cannot be undone.`,
              confirmText: "Delete Permanently",
              cancelText: "Cancel",
              variant: "danger",
            });
            if (!okDelete) return;
            try {
              const res = await deleteProgram(String(selectedProgram.id)).unwrap();
              if (res.status) {
                showMessage(res.message || "Program deleted.");
              } else {
                showErrorMessage(res.message || "Failed to delete program.");
              }
            } catch {
              showErrorMessage("Failed to delete program.");
            }
          }}
          sx={{ color: "error.main" }}
        >
          <DeleteOutlineSharpIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
        <MenuItem
          onClick={async () => {
            handleProgramMenuClose();
            if (!selectedProgram) return;
            const isLocked = !!(selectedProgram as any).isLocked;
            try {
              const res = await (isLocked
                ? unlockProgram(selectedProgram.id).unwrap()
                : lockProgram(selectedProgram.id).unwrap());
              if (res.status) showMessage(res.message || (isLocked ? "Program unlocked." : "Program locked."));
              else showErrorMessage(res.message || "Lock toggle failed.");
            } catch {
              showErrorMessage("Lock toggle failed.");
            }
          }}
        >
          <LockOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          {(selectedProgram as any)?.isLocked ? "Unlock program" : "Lock program"}
        </MenuItem>
        <MenuItem
          onClick={async () => {
            handleProgramMenuClose();
            if (!selectedProgram) return;
            const folderPath = (() => {
              const trail: string[] = [];
              const findFolder = (list: IFolder[], id: string | number | null | undefined): boolean => {
                if (!id) return false;
                for (const f of list) {
                  if (String(f.id) === String(id)) { trail.push(f.folderName); return true; }
                  if (f.children?.length && findFolder(f.children, id)) { trail.unshift(f.folderName); return true; }
                }
                return false;
              };
              findFolder(folders, selectedProgram.folderId);
              return trail.join(" / ") || "(no folder)";
            })();
            const author = (selectedProgram as any).authorName ?? "(unknown)";
            const text = [
              `Program: ${selectedProgram.programName}`,
              `ID: ${selectedProgram.id}`,
              `Folder: ${folderPath}`,
              `Owner: ${author}`,
            ].join("\n");
            try {
              await navigator.clipboard.writeText(text);
              showMessage("Support info copied to clipboard.");
            } catch {
              showErrorMessage("Copy failed - your browser blocked clipboard access.");
            }
          }}
        >
          <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} /> Copy support info to clipboard
        </MenuItem>
      </Menu>

      {/* Copy to clients (Batch 29) */}
      <CopyToClientsDialog
        open={copyToClientsDialogOpen}
        program={selectedProgram as any}
        onClose={() => setCopyToClientsDialogOpen(false)}
      />

      {/* Batch 1A "1A.7 " bulk copy variant. Same dialog, populated
         with every selected program; on save, the picked-student set
         is applied to each in turn. */}
      <CopyToClientsDialog
        open={bulkCopyDialogOpen}
        program={null}
        bulkPrograms={
          Array.from(selectedProgramIds)
            .map((id) => programs.find((p) => String(p.id) === id))
            .filter(Boolean)
            .map((p) => ({ id: Number((p as any).id), programName: (p as any).programName })) as any
        }
        onClose={() => setBulkCopyDialogOpen(false)}
        onCopied={() => clearSelection()}
      />

      {/* Batch 61 "5 " TemplatesDrawer replaced by /aba-therapy/settings/templates page. */}
      <ImportProgramsDialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} />


      {/* Create Folder Dialog - Batch 30: shows the destination path */}
      <Dialog
        open={newFolderDialogOpen}
        onClose={() => setNewFolderDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Folder Name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            sx={{ mt: 0.5 }}
            onKeyDown={(e) => { if (e.key === "Enter") handleCreateFolder(); }}
          />
          <Autocomplete
            size="small"
            sx={{ mt: 1.5 }}
            options={flatFolders}
            value={flatFolders.find((o) => o.id === (parentForNewFolder ?? null)) ?? flatFolders[0]}
            onChange={(_, v) => setParentForNewFolder(v?.id ?? null)}
            getOptionLabel={(o) => o?.label ?? ""}
            isOptionEqualToValue={(a, b) => (a?.id ?? null) === (b?.id ?? null)}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ pl: 2 + option.depth * 2 }} data-testid="list-parent-option">
                {option.id === null ? <strong>{option.label}</strong> : option.label}
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Parent Folder"
                placeholder="Select where this folder should be created" />
            )}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            Choose <strong>Top level (All Programs)</strong> to create a main folder, or select an existing folder to create a subfolder inside it.
          </Typography>
          {createFolderError && (
            <Typography variant="caption" color="error.main" sx={{ display: "block", mt: 1 }}>
              {createFolderError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewFolderDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateFolder} disabled={!newFolderName.trim()}>
            Create Folder
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Folder Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Folder Name"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            sx={{ mt: 1 }}
            onKeyDown={(e) => { if (e.key === "Enter") handleRenameFolder(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRenameFolder}
            disabled={!renameValue.trim() || renameValue.trim() === contextFolder?.folderName}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete-with-contents Dialog */}
      <Dialog open={!!deleteFolderState} onClose={() => setDeleteFolderState(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete folder</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>{deleteFolderState?.folder.folderName}</strong> contains{" "}
            {deleteFolderState?.counts.childFolders ?? 0} subfolder(s) and{" "}
            {deleteFolderState?.counts.programs ?? 0} program(s). Pick what happens to them:
          </Typography>
          <Stack spacing={1} sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={() => commitDeleteFolder("move_to_parent")}>
              Move contents up one level
            </Button>
            <Button variant="outlined" onClick={() => commitDeleteFolder("move_to_root")}>
              Move contents to library root
            </Button>
            <Button variant="outlined" color="error" onClick={() => commitDeleteFolder("delete_contents")}>
              Delete everything inside
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFolderState(null)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Move-folder Dialog (reuses tree-picker pattern) */}
      <Dialog open={moveFolderDialogOpen} onClose={() => setMoveFolderDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Move folder <em>{contextFolder?.folderName}</em>
        </DialogTitle>
        <DialogContent dividers>
          <List dense>
            <ListItemButton onClick={() => handleMoveFolder(null)}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <FolderOpenIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Top level (All Programs)" />
            </ListItemButton>
            {(() => {
              // Batch 32 carryover - the previous guard checked whether the
              // candidate was an ancestor of contextFolder, which is the
              // wrong direction: the backend rejects moving a folder into
              // one of its own DESCENDANTS. Precompute the descendant id
              // set so the correct rows are disabled up-front.
              const forbiddenIds = new Set<string>();
              if (contextFolder) {
                forbiddenIds.add(contextFolder.id);
                const walk = (f: IFolder) => {
                  forbiddenIds.add(f.id);
                  (f.children ?? []).forEach(walk);
                };
                (contextFolder.children ?? []).forEach(walk);
              }
              const render = (items: IFolder[], level: number): React.ReactNode =>
                items.map((f) => {
                  const isSelf = contextFolder ? f.id === contextFolder.id : false;
                  const isDesc = forbiddenIds.has(f.id) && !isSelf;
                  return (
                    <React.Fragment key={f.id}>
                      <ListItemButton
                        disabled={forbiddenIds.has(f.id)}
                        onClick={() => handleMoveFolder(f.id)}
                        sx={{ pl: 2 + level * 2 }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <FolderIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={f.folderName}
                          secondary={isSelf ? "Cannot move a folder into itself" : isDesc ? "Cannot move a folder into its own descendant" : undefined}
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItemButton>
                      {f.children && render(f.children, level + 1)}
                    </React.Fragment>
                  );
                });
              return render(folders, 0);
            })()}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveFolderDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <MoveProgramDialog
        open={moveProgramDialogOpen}
        onClose={() => setMoveProgramDialogOpen(false)}
        folders={folders}
        onMove={handleMoveProgram}
      />
    </Box>
  );
};

export default Index;
